/**
 * chat-read-receipts (2026-07-29) — 읽음/안읽음 계산 SSOT.
 *
 * 서버는 방마다 (userId → lastReadId) 맵만 준다. "메시지 M 을 아직 안 읽은 인원" 은
 * `lastReadId < M.id` 인 *타* 멤버 수로 여기서 계산한다. 멤버 1명이 읽으면 SSE `chat_read`
 * 한 건으로 맵의 항목 하나만 바뀌고, 화면의 모든 배지가 자동으로 정확히 재계산된다.
 *
 * id 는 BigInt 문자열이라 Number 로 바꾸면 2^53 을 넘는 순간 조용히 틀린다. 아래 비교자는
 * "음이 아닌 정수, 선행 0 없음"(백엔드 BigInt 직렬화 계약) 전제로 자릿수 → 사전순으로 비교한다.
 */

/** a > b (BigInt 문자열 비교). 비정상 입력은 false — 배지를 과다 표시하지 않는 안전한 쪽. */
export function isIdGreater(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!isNumericId(a) || !isNumericId(b)) return false;
  if (a!.length !== b!.length) return a!.length > b!.length;
  return a! > b!;
}

function isNumericId(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.length > 0 && /^[0-9]+$/.test(v);
}

/** 방 종류가 1:1 인가 — 표시 문구 분기(1:1 "읽음" vs 그룹 "모두 읽음")의 기준. */
export function isDirectRoom(roomType: string | null | undefined, memberCount: number): boolean {
  // type 이 'direct' 면 확정. 없거나 team/group 이어도 활성 멤버가 2명이면 사실상 1:1 로 본다
  // (그룹방에서 인원이 빠져 둘만 남은 경우 — 사용자 눈에는 1:1 이다).
  return roomType === 'direct' || memberCount === 2;
}

export interface UnreadCountArgs {
  /** 대상 메시지 id (BigInt 문자열). */
  messageId: string;
  /** 방의 (userId → lastReadId) 맵. */
  readStates: Record<string, string | null> | undefined;
  /** 본인 userId — 자기 자신은 미읽음 인원에서 제외. */
  myUserId: string;
}

/**
 * 이 메시지를 아직 읽지 않은 *타* 멤버 수.
 *
 * readStates 가 아직 없으면 -1 (= 알 수 없음) 을 돌려 호출부가 배지를 숨기게 한다.
 * 0 을 돌리면 "전원 읽음" 으로 오인돼 잘못된 '읽음' 표시가 뜬다.
 */
export function countUnreadMembers({ messageId, readStates, myUserId }: UnreadCountArgs): number {
  if (!readStates) return -1;
  const entries = Object.entries(readStates);
  if (entries.length === 0) return -1;
  let n = 0;
  for (const [userId, lastReadId] of entries) {
    if (userId === myUserId) continue;
    // lastReadId 가 messageId 이상이면 읽은 것. null(한 번도 안 읽음) 은 항상 미읽음.
    if (!isIdGreater(lastReadId, messageId) && lastReadId !== messageId) n++;
  }
  return n;
}

/** countUnreadMembers 로 미읽음인 멤버의 userId 목록 — 툴팁("누가 안 읽었나") 용. */
export function listUnreadUserIds({ messageId, readStates, myUserId }: UnreadCountArgs): string[] {
  if (!readStates) return [];
  const out: string[] = [];
  for (const [userId, lastReadId] of Object.entries(readStates)) {
    if (userId === myUserId) continue;
    if (!isIdGreater(lastReadId, messageId) && lastReadId !== messageId) out.push(userId);
  }
  return out;
}

/**
 * 미읽음 구분선을 붙일 메시지의 인덱스 (없으면 -1).
 *
 * anchor = 방 **진입 시점** 의 내 lastReadId. 그보다 큰 id 를 가진 가장 오래된 *타인* 메시지가
 * "안 읽은 첫 메시지" 다. 내가 보낸 메시지는 기준이 되지 않는다 — 방에 머무는 동안 내가 새로
 * 보낸 메시지도 anchor 보다 크지만, 그 위에 "여기까지 읽었습니다" 가 뜨면 안 된다.
 *
 * messages 는 시간 오름차순(= id 오름차순) 이라고 가정한다 (chatStore 가 commitSeq 로 정렬 보관).
 */
export function findFirstUnreadIndex(
  messages: ReadonlyArray<{ id: string; senderId: string | null; isAi?: boolean }>,
  anchor: string | null | undefined,
  myUserId: string,
): number {
  if (anchor === undefined) return -1;   // 아직 read-states 조회 전 — 표시 안 함
  const base = anchor ?? '0';
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i]!;
    if (m.senderId === myUserId) continue;         // 내 메시지는 기준 아님
    if (m.senderId === null && !m.isAi) continue;  // 시스템 메시지(입장/퇴장 등) 는 기준 아님
    if (isIdGreater(m.id, base)) return i;
  }
  return -1;
}
